import { FC, useMemo, useState } from "react";
import _omit from "lodash/omit";
import { toast } from "react-hot-toast";

import {
  ACCOUNT_CONFIRM_EDIT_NEXT_TOUCH_TIME_MODAL_ID,
  AccountConfirmEditNextTouchTimeConfirmationModal,
} from "@/components/modals/account-edit-touch-time-confirmation-modal";
import {
  NEXT_TOUCH_TIME_OPTIONS,
  NEXT_TOUCH_TIME_OPTIONS_DAYJS_DURATION_MAP,
  NEXT_TOUCH_TIME_OPTIONS_MAP,
} from "shared/lib/constants/dispositions";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import {
  DATE_TIME_CALENDAR_MODAL_ID,
  DateTimeCalendarModal,
} from "@/modals/date-time-calendar-modal";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { DayJs } from "shared/lib/helpers/date";
import { useEditNextTouchTime } from "@/api/routes/account/mutations";
import { CampaignI } from "@/lib/types/campaign";
import { ActionMenu, ActionMenuActionI } from "shared/ui/action-menu";
import { dispatchCustomEvent } from "@/helpers/events";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { AccountI } from "shared/lib/interfaces/account";

import { swallow } from "@/api/legacy-swallow";

type NextTouchTimeI = ValueOfObjectFields<typeof NEXT_TOUCH_TIME_OPTIONS>;

interface SetNextTouchTimeDropdownPropsI {
  className?: string;
  campaign?: CampaignI;
  account?: AccountI;
}

const calculateNextTouchTime = (selectedOption?: NextTouchTimeI) => {
  if (
    !selectedOption ||
    [NEXT_TOUCH_TIME_OPTIONS.CUSTOM as string].includes(selectedOption)
  ) {
    return undefined;
  }

  return DayJs().add(
    DayJs.duration(NEXT_TOUCH_TIME_OPTIONS_DAYJS_DURATION_MAP[selectedOption])
  );
};

export const SetNextTouchTimeDropdown: FC<SetNextTouchTimeDropdownPropsI> = ({
  className,
  campaign,
  account,
}) => {
  const { onAccountUpdate } = useAccountDetailsContext();
  const { mutateAsync: editNextTouchTime } = useEditNextTouchTime();

  const [selectedNextTouchTime, setSelectedNextTouchTime] =
    useState<NextTouchTimeI>();

  // Click handler for the dropdown options, works for all static options and custom one.
  const handleConfirmChangeNextTouchTime = (value: NextTouchTimeI) => {
    if (value === NEXT_TOUCH_TIME_OPTIONS.CUSTOM) {
      modalHelpers.open(DATE_TIME_CALENDAR_MODAL_ID);
    } else {
      setSelectedNextTouchTime(value);
      setTimeout(() => {
        modalHelpers.open(ACCOUNT_CONFIRM_EDIT_NEXT_TOUCH_TIME_MODAL_ID);
      }, 100);
    }
  };

  const actionItems: ActionMenuActionI[] = useMemo(
    () =>
      Object.values(_omit(NEXT_TOUCH_TIME_OPTIONS, ["NONE"])).map((value) => ({
        title: NEXT_TOUCH_TIME_OPTIONS_MAP[value],
        handler: () =>
          handleConfirmChangeNextTouchTime(value as NextTouchTimeI),
      })),
    [handleConfirmChangeNextTouchTime]
  );

  // Handler for custom date time modal. CalendarForm already combined the
  // selected date, time and timezone into `date` (offset-bearing ISO) -
  // re-combining it here would corrupt the instant (GLE-8091).
  const onSetCustomNextTouchTime = async (date: string) => {
    const customNextTouchTimeISO = DayJs(date).toISOString();

    modalHelpers.close(DATE_TIME_CALENDAR_MODAL_ID);

    await updateNextTouchTime(customNextTouchTimeISO);

    //To make sure next touch time on the list is also updated
    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.ACCOUNT.RELOAD_COMPLETE,
      data: { campaign, account },
    });
  };

  const handlePreselectedValueConfirmation = async () => {
    await updateNextTouchTime(
      calculateNextTouchTime(selectedNextTouchTime)?.toISOString() as string
    );

    //To make sure next touch time on the list is also updated
    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.ACCOUNT.RELOAD_COMPLETE,
      data: { campaign, account },
    });
  };

  // This function should be called as the final one after user confirms the change
  // of next touch time either from confirmation modal or custom date time modal.
  const updateNextTouchTime = (nextTouchTimeISO: string) => {
    if (!campaign?.id || !account?.id || !nextTouchTimeISO) {
      return toast.error(
        "Campaign or account IDs are not valid, or next touch time is not selected, please try again."
      );
    }

    //To make sure next touch time on the list is also updated
    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.ACCOUNT.RELOAD_COMPLETE,
      data: { campaign, account },
    });

    return swallow(
      editNextTouchTime(
        {
          campaignId: campaign.id,
          accountId: account.id,
          nextTouchTime: nextTouchTimeISO,
        },
        {
          onSuccess: () => {
            toast.success("Next touch time has been updated!");
            onAccountUpdate();
          },
          onError: () => {
            toast.error("Failed to update next touch time, please try again.");
          },
        }
      )
    );
  };

  return (
    <>
      <ActionMenu
        className={className}
        menuClassName="overflow-y-auto"
        trigger={
          <button className="ae-link brand-typography-body6">Edit</button>
        }
        itemClassName="w-max min-w-[120px]"
        actions={actionItems}
      />

      <AccountConfirmEditNextTouchTimeConfirmationModal
        formattedNextTouchTime={calculateNextTouchTime(
          selectedNextTouchTime
        )?.format("MMM D, YYYY, h:mm A")}
        onConfirm={handlePreselectedValueConfirmation}
      />

      <DateTimeCalendarModal
        title="Set Next Touch Time"
        descritpion="Specify date and time when to remind you to call back"
        onSuccess={onSetCustomNextTouchTime}
      />
    </>
  );
};
